// src/pages/UnsoldTicketWarehouseReport/index.tsx
// US-019 — Lưu trữ vật lý cho phiếu thu hồi vé ế đã thẩm định & phê duyệt.
// Vai trò: Nhân viên Nghiệp vụ / Ban Lãnh đạo. Layout bám theo trang /issuance-plans.
//
// Mục 8.e/8.f, README_UPDATE.md:
// - Bỏ hẳn Card "Báo cáo nhanh thu hồi vé ế" (mục 8.e) — báo cáo tổng hợp đã có ở /reports (mục 24,
//   REQUIRE_FIXBUGS.md), card này chỉ trùng lặp. Ô lọc "Kỳ phát hành" cũng bỏ theo vì trước đây chỉ
//   phục vụ riêng card báo cáo nhanh (API danh sách bên dưới không hỗ trợ lọc theo kỳ).
// - Chuyển nguồn dữ liệu của Card còn lại sang TicketRecall (Status = Approved) thay vì
//   TicketReturnReconciliation (luồng cũ đã ngừng dùng, không còn dữ liệu — xem
//   UnsoldTicketAppraisal/index.tsx). Đây giờ thuần là 1 báo cáo (không cần Redux slice riêng,
//   cùng cách tiếp cận với pages/Reports/index.tsx) — thao tác ngoài bảng chỉ có "Xem chi tiết",
//   việc gán vị trí kho chuyển vào bên trong modal chi tiết.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, buildAgentMap, loadAgentsWithCache } from 'helpers/agentLookup';
import { getTicketRecallsApi } from 'helpers/fakebackend_helper';
import { IAgent } from 'slices/agentManagement/types';
import { TICKET_RECALL_STATUS, TicketRecall, TicketRecallPagination } from 'pages/TicketRecall/types';
import { useBrandingTitle } from '../../hooks';

import ApprovedRecallWarehouseList from './components/ApprovedRecallWarehouseList';
import RecallWarehouseDetailModal from './modals/RecallWarehouseDetailModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_warehouse_report');

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const getPayloadTotal = (response: any): number => {
  const payload = response?.data || response;
  return payload?.totalCount ?? payload?.totalItems ?? 0;
};

// Mục 11.c, README_UPDATE.md — loại bỏ param rỗng trước khi gọi API. Thiếu bước này, AgentId
// undefined bị api_helper nối thẳng vào query string thành chuỗi "undefined", khiến backend trả
// lỗi 400 (model binder không parse được "undefined" cho kiểu long?) mỗi khi bỏ trống bộ lọc đại lý.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const DEFAULT_PAGE_SIZE = 20;

const UnsoldTicketWarehouseReport: React.FC = () => {
  useBrandingTitle(st('luu_kho_ve_thu_hoi_text'));

  const location = useLocation();

  const [list, setList] = useState<TicketRecall[]>([]);
  const [pagination, setPagination] = useState<TicketRecallPagination>({
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState<TicketRecall | null>(null);

  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  useEffect(() => {
    loadAgentsWithCache().then(setAgents);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTicketRecallsApi(
        cleanParams({
          Status: TICKET_RECALL_STATUS.Approved,
          AgentId: agentId || undefined,
          PageIndex: pagination.currentPage,
          PageSize: pagination.pageSize,
        }),
      );
      const items = getPayloadItems(response) as TicketRecall[];
      const totalItems = getPayloadTotal(response);
      setList(items);
      setPagination((prev) => ({
        ...prev,
        totalItems,
        totalPages: Math.max(1, Math.ceil(totalItems / prev.pageSize)),
      }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleClearFilters = () => {
    setAgentId('');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleViewDetail = (item: TicketRecall) => {
    setDetailTarget(item);
  };

  const handleSaved = () => {
    setDetailTarget(null);
    loadList();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('luu_kho_ve_thu_hoi_text')} pageTitle={st('thu_hoi_ve_e_text')} />

          <Card id="approvedRecallWarehouseList">
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('phieu_da_tham_dinh_phe_duyet_title')}</h5>
                </div>
                <div className="col-sm-auto">
                  <button
                    type="button"
                    className="btn btn-soft-secondary"
                    disabled={loading}
                    onClick={handleClearFilters}
                  >
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    {st('xoa_loc_button')}
                  </button>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end mb-5">
                <Col md={4}>
                  <Label className="form-label">{st('dai_ly_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={agentId}
                    data-action-code="filter-input-agent"
                    data-action-name="Lọc theo đại lý"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-agent')}
                    onChange={(event) => {
                      setAgentId(event.target.value);
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                  >
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.code} - {agent.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={8} className="text-md-end">
                  <span className="text-muted">
                    {st('tong_text')} <span className="fw-semibold">{pagination.totalItems}</span> {st('phieu_da_duyet_text')}
                  </span>
                </Col>
              </Row>

              <ApprovedRecallWarehouseList
                items={list}
                pagination={pagination}
                agentMap={agentMap}
                loading={loading}
                onViewDetail={handleViewDetail}
              />

              <AgentPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      <RecallWarehouseDetailModal
        isOpen={!!detailTarget}
        item={detailTarget}
        agentMap={agentMap}
        onClose={() => setDetailTarget(null)}
        onSaved={handleSaved}
      />
    </React.Fragment>
  );
};

export default UnsoldTicketWarehouseReport;
